import { Grid } from "@mui/material";
import { useEffect, useState } from "react";

import Modal from "../Modal";
import FormField from "../FormField";
import Autocomplete from "../Autocomplete";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import AddressService from "../../services/common/address.service";

import { IAddress, ICodename } from "../../commons/interfaces";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";



interface IPropsModalAddress {
    open: boolean;
    dataAddress?: IAddress;
    requiredInputProvince?: boolean;
    requiredInputPostalCode?: boolean;

    onClose: () => void;
    onAction: (dataAddress: IAddress) => void;
}

const ModalAddress = (props: IPropsModalAddress): JSX.Element => {

    const strings = useStrings();
    const {
        userInfo,
        tenantCode,

        onError,
        onSuccess,
        onShowLoading,
        onHideLoading,
    } = useCommonComponentContext();

    const [dataCountries, setDataCountries] = useState<ICodename[]>([]);
    const [dataError, setDataError] = useState<IAddress>({} as IAddress);
    const [dataAddress, setDataAddress] = useState<IAddress>({} as IAddress);

    useEffect(() => {
        getAllDataCountries();
    }, []);

    useEffect(() => {
        if (props.open) {
            setDataError({});
            setDataAddress((prev) => ({
                ...prev,
                ...props.dataAddress,
            }));
        }
    }, [props.dataAddress, props.open]);

    const getAllDataCountries = async () => {
        try {
            const result: any[] = await AddressService.getCountries({ type: 1 });
            const newData: ICodename[] = result.map((item: any) => {
                return {
                    code: item.id,
                    name: item.name,
                };
            });
            setDataCountries(newData);
        } catch (error) { setDataCountries([]); }
    };

    const onClose = () => {
        setDataError({});
        setDataAddress({});

        props.onClose();
    }

    const onAction = async () => {
        if (!checkedValidate()) return;

        try {
            onShowLoading();

            const addressParams: any = {
                tenantCode: tenantCode,
                organizationId: userInfo?.userProfile?.organizationId || "0",

                postalCode: dataAddress.postalCode,
                country: dataAddress.country,
                cityName: dataAddress.cityName,
                provinceName: dataAddress.provinceName,
                districtName: dataAddress.districtName,
                wardName: dataAddress.wardName,
                addressLine2: dataAddress.addressLine2,
                addressLine: dataAddress.addressLine,
            };


            let resultAddresses: any = {};
            if (Helpers.isNullOrEmpty(dataAddress.id)) {
                resultAddresses = await AddressService.createAddress(addressParams);
            } else {
                resultAddresses = await AddressService.updateAddress({
                    ...addressParams,
                    id: dataAddress.id,
                });
            }

            if (resultAddresses.statusCode === 200) {
                // const msg = Helpers.isNullOrEmpty(dataAddress.id)
                //     ? strings.Profile.CREATE_ADDRESS_SUCCESS
                //     : strings.Profile.UPDATE_ADDRESS_SUCCESS;

                const countryName = dataCountries.find(el => `${el.code}` === `${dataAddress.country}`)?.name || "";

                props.onAction({
                    ...resultAddresses.result,
                    countryName: countryName,
                });

                // onSuccess(msg);
            }
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        } finally {
            onHideLoading();
        }
    }

    const onChangeValue = (newValue: any, key: string) => {
        setDataError(prev => ({
            ...prev,
            [key]: undefined,
        }));

        setDataAddress(prev => ({
            ...prev,
            [key]: newValue,
        }));
    }

    const checkedValidate = () => {
        let isValid = true;
        let newDataError: any = { ...dataError };

        if (Helpers.isNullOrEmpty((dataAddress as any)["country"])) {
            isValid = false;
            newDataError["country"] = strings.Common.REQUIRED;
        }

        if (props.requiredInputProvince
            && Helpers.isNullOrEmpty((dataAddress as any)["provinceName"])) {
            isValid = false;
            newDataError["provinceName"] = strings.Common.REQUIRED;
        }

        if (props.requiredInputPostalCode &&
            Helpers.isNullOrEmpty((dataAddress as any)["postalCode"])) {
            isValid = false;
            newDataError["postalCode"] = strings.Common.REQUIRED;
        }

        if (!isValid) {
            setDataError((prev) => ({
                ...prev,
                ...newDataError,
            }));
        }

        return isValid;
    };

    return (
        <Modal
            fullWidth
            maxWidth={"md"}
            hasActionButton
            onClose={onClose}
            onAction={onAction}
            visible={props.open}
            buttonAction={strings.Common.SAVE}
            closeButton={strings.Common.CANCEL}
            title={strings.Profile.ADDRESS_INFO}
        >
            <Grid container spacing={3} p={2}>
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        required
                        data={dataCountries || []}
                        label={strings.Profile.COUNTRY}
                        errorMessage={dataError?.country}
                        defaultValue={dataAddress?.country}
                        placeholder={strings.Profile.SELECT_COUNTRY}
                        onChange={(value: any) => {
                            onChangeValue(value, "country");
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormField
                        maxLength={255}
                        errorMessage={dataError?.provinceName}
                        required={props.requiredInputProvince}
                        defaultValue={dataAddress?.provinceName}
                        label={strings.Profile.PROVINCE_STATE_NAME}
                        placeholder={strings.Profile.ENTER_PROVINCE_STATE_NAME}
                        onBlur={(value: string) => {
                            onChangeValue(value, "provinceName");
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormField
                        maxLength={255}
                        label={strings.Profile.CITY_NAME}
                        defaultValue={dataAddress?.cityName}
                        placeholder={strings.Profile.ENTER_CITY_NAME}
                        onBlur={(value: string) => {
                            onChangeValue(value, "cityName");
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormField
                        maxLength={255}
                        label={strings.Profile.DISTRICT_NAME}
                        defaultValue={dataAddress?.districtName}
                        placeholder={strings.Profile.ENTER_DISTRICT_NAME}
                        onBlur={(value: string) => {
                            onChangeValue(value, "districtName");
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormField
                        maxLength={255}
                        label={strings.Profile.WARD_NAME}
                        defaultValue={dataAddress?.wardName}
                        placeholder={strings.Profile.ENTER_WARD_NAME}
                        onBlur={(value: string) => {
                            onChangeValue(value, "wardName");
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormField
                        type="number"
                        maxLength={6}
                        label={strings.Profile.POSTAL_CODE}
                        errorMessage={dataError?.postalCode}
                        defaultValue={dataAddress?.postalCode}
                        required={props.requiredInputPostalCode}
                        placeholder={strings.Profile.ENTER_POSTAL_CODE}
                        onBlur={(value: string) => {
                            onChangeValue(value, "postalCode");
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormField
                        maxLength={255}
                        label={strings.Profile.ADDRESS_LINE}
                        defaultValue={dataAddress?.addressLine}
                        placeholder={strings.Profile.ENTER_ADDRESS_LINE}
                        onBlur={(value: string) => {
                            onChangeValue(value, "addressLine");
                        }}
                    />
                </Grid>
            </Grid>
        </Modal>
    );
};

export default ModalAddress;